import React, { useEffect, useState } from 'react';
import { getMouvementsStock, getProduits, entreeStock, sortieStock, getFournisseurs, getEntrepots, getEmplacementsProduit } from '../services/api';
import {
  TrendingUp, TrendingDown, Filter, Package, Warehouse,
  AlertCircle, CheckCircle, X, Check, Loader,
  ArrowUpCircle, ArrowDownCircle, DollarSign, Search
} from 'lucide-react';
import moment from 'moment';
import 'moment/locale/fr';
import { useBoutique } from '../context/BoutiqueContext';
import { useAuth } from '../context/AuthContext';
import BoutiqueSelector from '../components/BoutiqueSelector';
import './Stock.css';

moment.locale('fr');

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(n || 0);

const MOTIF_LABELS = { achat: 'Achat', retour: 'Retour', vente: 'Vente', perte: 'Perte', casse: 'Casse', ajustement: 'Ajustement' };

const Stock = () => {
  const { user } = useAuth();
  const { selectedBoutiqueId, boutiques } = useBoutique();

  const [mouvements,         setMouvements]         = useState([]);
  const [filteredMouvements, setFilteredMouvements] = useState([]);
  const [produits,           setProduits]           = useState([]);
  const [filteredProduits,   setFilteredProduits]   = useState([]);
  const [fournisseurs,       setFournisseurs]       = useState([]);
  const [entrepots,          setEntrepots]          = useState([]);
  const [filteredEntrepots,  setFilteredEntrepots]  = useState([]);
  const [showModal,          setShowModal]          = useState(false);
  const [typeOperation,      setTypeOperation]      = useState('entree');
  const [selectedProduit,    setSelectedProduit]    = useState(null);
  const [filterType,         setFilterType]         = useState('');
  const [searchTerm,         setSearchTerm]         = useState('');
  // Recherche dans le sélecteur de produit du modal
  const [produitSearch,      setProduitSearch]      = useState('');
  const [showProduitList,    setShowProduitList]    = useState(false);
  const [submitting,         setSubmitting]         = useState(false);
  // Emplacements du produit sélectionné, par entrepôt (destination d'une entrée)
  const [emplacementsProduit, setEmplacementsProduit] = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [toast,              setToast]              = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    produit: '', quantite: '', prixUnitaire: '', motif: '',
    reference: '', commentaire: '', typeVente: 'detail',
    boutiqueIdModal: '', fournisseur: '', entrepot: '', emplacement: ''
  });

  useEffect(() => { loadData(); }, [filterType, selectedBoutiqueId]); // eslint-disable-line
  useEffect(() => { filterMouvementsAndProduits(); }, [mouvements, produits, selectedBoutiqueId, formData.boutiqueIdModal, searchTerm]); // eslint-disable-line

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 3500);
  };

  const loadData = async () => {
    try {
      const params = filterType ? { type: filterType } : {};
      const [mr, pr, fr, er] = await Promise.all([
        getMouvementsStock(params), getProduits(), getFournisseurs(true), getEntrepots()
      ]);
      setMouvements(mr.data.data);
      setProduits(pr.data.data);
      setFournisseurs(fr.data.data || []);
      setEntrepots(er.data.data || []);
    } catch { showToast('error', 'Erreur lors du chargement'); }
    finally   { setLoading(false); }
  };

  const filterMouvementsAndProduits = () => {
    let fm = mouvements;
    if (selectedBoutiqueId && selectedBoutiqueId !== 'all')
      fm = fm.filter(m => m.boutiqueId === selectedBoutiqueId);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      fm = fm.filter(m =>
        (m.produit?.nom || '').toLowerCase().includes(q) ||
        (m.produit?.codeProduit || '').toLowerCase().includes(q) ||
        (m.produit?.codeBarres || '').toLowerCase().includes(q) ||
        (m.reference || '').toLowerCase().includes(q)
      );
    }
    setFilteredMouvements(fm);

    const bid = formData.boutiqueIdModal || selectedBoutiqueId;
    let fp = produits;
    if (bid && bid !== 'all') fp = fp.filter(p => p.boutiqueId === bid);
    setFilteredProduits(fp);

    setFilteredEntrepots(entrepots.filter(e => e.actif));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (typeOperation === 'entree') {
        await entreeStock(formData);
        showToast('success', 'Entrée de stock enregistrée');
      } else {
        await sortieStock(formData);
        showToast('success', 'Sortie de stock enregistrée');
      }
      setShowModal(false); resetForm(); loadData();
    } catch (err) { showToast('error', err.response?.data?.message || 'Une erreur est survenue'); }
    finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setFormData({ produit:'', quantite:'', prixUnitaire:'', motif:'', reference:'', commentaire:'', typeVente:'detail', boutiqueIdModal: selectedBoutiqueId || '', fournisseur:'', entrepot:'', emplacement:'' });
    setSelectedProduit(null);
    setProduitSearch('');
    setShowProduitList(false);
    setEmplacementsProduit([]);
  };

  const openModal = (type) => {
    setTypeOperation(type);
    setFormData({ produit:'', quantite:'', prixUnitaire:'', motif:'', reference:'', commentaire:'', typeVente:'detail', boutiqueIdModal: selectedBoutiqueId || '', fournisseur:'', entrepot:'', emplacement:'' });
    setSelectedProduit(null);
    setProduitSearch('');
    setShowProduitList(false);
    setEmplacementsProduit([]);
    setShowModal(true);
  };

  // Prix du produit pour l'opération : achat (entrée) ou vente (sortie), selon le type
  // de vente, avec repli sur le prix générique. Un prix à 0 reste affiché 0.
  const prixProduit = (p, tv, op = typeOperation) => {
    const [spec, gen] = op === 'entree'
      ? [tv === 'gros' ? p.prixAchatGros : p.prixAchatDetail, p.prixAchat]
      : [tv === 'gros' ? p.prixVenteGros : p.prixVenteDetail, p.prixVente];
    if (Number(spec) > 0) return spec;
    if (Number(gen) > 0) return gen;
    return spec ?? gen ?? 0;
  };

  const handleProduitChange = (id) => {
    const p = filteredProduits.find(pr => pr._id === id);
    setSelectedProduit(p);
    let typeVente = 'detail', prix = '';
    if (p) {
      typeVente = p.typeVente || 'detail';
      prix = prixProduit(p, typeVente);
    }
    setFormData({ ...formData, produit: id, typeVente, prixUnitaire: prix, emplacement: '' });
    setProduitSearch(p ? p.nom : '');
    setShowProduitList(false);
    loadEmplacementsProduit(id);
  };

  // Entrée : charger les emplacements du produit dans les entrepôts où il est
  // présent, et présélectionner son entrepôt + emplacement habituel.
  const loadEmplacementsProduit = async (produitId) => {
    setEmplacementsProduit([]);
    if (!produitId || typeOperation !== 'entree') return;
    try {
      const { data } = await getEmplacementsProduit(produitId);
      const list = data.data || [];
      setEmplacementsProduit(list);
      const first = list[0];
      if (first) {
        setFormData(f => f.produit === produitId && !f.entrepot
          ? { ...f, entrepot: String(first.entrepotId), emplacement: first.habituelId ? String(first.habituelId) : 'non-range' }
          : f);
      }
    } catch { /* non bloquant */ }
  };

  // Emplacement par défaut d'un entrepôt pour le produit : l'habituel, sinon « non rangé »
  const emplacementParDefaut = (entrepotId) => {
    const ep = emplacementsProduit.find(x => String(x.entrepotId) === String(entrepotId));
    return ep?.habituelId ? String(ep.habituelId) : 'non-range';
  };

  const handleEntrepotChange = (entrepotId) => {
    setFormData({ ...formData, entrepot: entrepotId, emplacement: entrepotId ? emplacementParDefaut(entrepotId) : '' });
  };

  // Options d'emplacement de l'entrepôt choisi : ceux du produit d'abord, puis le reste du plan
  const emplacementOptions = (() => {
    const ent = entrepots.find(e => String(e._id) === String(formData.entrepot));
    const ep  = emplacementsProduit.find(x => String(x.entrepotId) === String(formData.entrepot));
    const duProduit = (ep?.emplacements || []).filter(x => x.emplacementId);
    const ids = new Set(duProduit.map(x => String(x.emplacementId)));
    const autres = [];
    for (const r of ent?.rayons || []) {
      for (const emp of r.emplacements || []) {
        if (emp.actif !== false && !ids.has(String(emp._id))) autres.push({ emplacementId: emp._id, nom: emp.nom, rayonNom: r.nom });
      }
    }
    return { duProduit, autres, habituelId: ep?.habituelId };
  })();

  const libelleEmp = (x) => `${x.rayonNom ? `${x.rayonNom} › ` : ''}${x.nom}`;

  // Produits du modal filtrés par la recherche
  const produitsModalSearched = (() => {
    if (!produitSearch.trim()) return filteredProduits;
    const q = produitSearch.toLowerCase().trim();
    return filteredProduits.filter(p =>
      (p.nom || '').toLowerCase().includes(q) ||
      (p.codeProduit || '').toLowerCase().includes(q) ||
      (p.codeBarres || '').toLowerCase().includes(q)
    );
  })();

  const handleTypeVenteChange = (tv) => {
    const p = filteredProduits.find(pr => pr._id === formData.produit);
    let prix = formData.prixUnitaire;
    if (p) prix = prixProduit(p, tv);
    setFormData({ ...formData, typeVente: tv, prixUnitaire: prix });
  };

  /* KPIs */
  const nbEntrees  = filteredMouvements.filter(m => m.type === 'entree').length;
  const nbSorties  = filteredMouvements.filter(m => m.type === 'sortie').length;
  const valTotale  = filteredMouvements.reduce((s, m) => s + (m.valeurTotale || 0), 0);

  if (loading) return (
    <div className="sk-loading"><Loader className="sk-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="sk-page">

      {/* Toast */}
      {toast.text && (
        <div className={`sk-toast sk-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="sk-header">
        <div className="sk-header-left">
          <div className="sk-header-icon"><Warehouse size={20}/></div>
          <div>
            <h1 className="sk-header-title">Mouvements de Stock</h1>
            <p className="sk-header-sub">Historique des entrées et sorties de produits</p>
          </div>
        </div>
        <div className="sk-header-right">
          {['admin-entreprise','super-admin'].includes(user?.role) && <BoutiqueSelector/>}
          <button className="sk-btn-entree" onClick={() => openModal('entree')}>
            <TrendingUp size={14}/> Entrée
          </button>
          <button className="sk-btn-sortie" onClick={() => openModal('sortie')}>
            <TrendingDown size={14}/> Sortie
          </button>
        </div>
      </div>

      <div className="sk-content">

        {/* KPI row */}
        <div className="sk-kpi-row">
          {[
            { label: 'Total mouvements', value: filteredMouvements.length, color: '#6366f1', bg: '#eef2ff',  icon: Package },
            { label: 'Entrées',          value: nbEntrees,                  color: '#10b981', bg: '#ecfdf5',  icon: ArrowUpCircle },
            { label: 'Sorties',          value: nbSorties,                  color: '#ef4444', bg: '#fef2f2',  icon: ArrowDownCircle },
            { label: 'Valeur totale',    value: fmt(valTotale),             color: '#f59e0b', bg: '#fffbeb',  icon: DollarSign },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="sk-kpi" style={{ borderTopColor: k.color }}>
                <div className="sk-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={15}/></div>
                <div className="sk-kpi-val">{k.value}</div>
                <div className="sk-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filter bar */}
        <div className="sk-filters">
          <div className="sk-search-wrap">
            <Search size={13} className="sk-search-ico"/>
            <input
              type="text"
              className="sk-search-input"
              placeholder="Rechercher un produit (nom, code, code-barres, référence)…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button type="button" className="sk-search-clear"
                onClick={() => setSearchTerm('')} title="Effacer">
                <X size={12}/>
              </button>
            )}
          </div>
          <div className="sk-filter-type">
            <Filter size={14} style={{ color: '#94a3b8', flexShrink: 0 }}/>
            <select className="sk-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">Tous les types</option>
              <option value="entree">Entrées uniquement</option>
              <option value="sortie">Sorties uniquement</option>
            </select>
          </div>
          <span className="sk-count">{filteredMouvements.length} résultat{filteredMouvements.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        <div className="sk-card">
          <table className="sk-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Produit</th>
                <th>Motif</th>
                <th>Fournisseur / Entrepôt</th>
                <th>Type vente</th>
                <th>Quantité</th>
                <th>Prix unit.</th>
                <th>Valeur</th>
                <th>Stock avant → après</th>
              </tr>
            </thead>
            <tbody>
              {filteredMouvements.length === 0 ? (
                <tr><td colSpan="10" className="sk-empty-row">Aucun mouvement trouvé</td></tr>
              ) : filteredMouvements.map(m => (
                <tr key={m._id}>
                  <td className="sk-date">{moment(m.createdAt).format('DD/MM/YY HH:mm')}</td>
                  <td>
                    {m.type === 'entree'
                      ? <span className="sk-type-badge sk-type-badge--in"><TrendingUp size={11}/> Entrée</span>
                      : <span className="sk-type-badge sk-type-badge--out"><TrendingDown size={11}/> Sortie</span>}
                  </td>
                  <td className="sk-prod-name">{m.produit?.nom}</td>
                  <td><span className="sk-motif">{MOTIF_LABELS[m.motif] || m.motif}</span></td>
                  <td>
                    <div className="sk-meta">
                      {m.fournisseur && <span className="sk-pill sk-pill--sup">{m.fournisseur.nom}</span>}
                      {m.entrepot    && <span className="sk-pill sk-pill--wh">{m.entrepot.nom}</span>}
                      {!m.fournisseur && !m.entrepot && <span className="sk-muted">—</span>}
                    </div>
                  </td>
                  <td>
                    <span className={`sk-type-vente ${m.typeVente === 'gros' ? 'sk-type-vente--gros' : 'sk-type-vente--detail'}`}>
                      {m.typeVente === 'gros' ? 'Gros' : 'Détail'}
                    </span>
                  </td>
                  <td className={`sk-qty sk-qty--${m.type}`}>
                    {m.type === 'entree' ? '+' : '−'}{m.quantite}
                  </td>
                  <td className="sk-price">{fmt(m.prixUnitaire)}</td>
                  <td className="sk-price sk-price--bold">{fmt(m.valeurTotale)}</td>
                  <td>
                    <span className="sk-stock-before">{m.stockAvant}</span>
                    <span className="sk-stock-arrow"> → </span>
                    <span className={`sk-stock-after sk-stock-after--${m.type}`}>{m.stockApres}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <>
          <div className="sk-overlay" onClick={() => { setShowModal(false); resetForm(); }}/>
          <div className="sk-modal">
            <div className={`sk-modal-head sk-modal-head--${typeOperation}`}>
              <div>
                <h2 className="sk-modal-title">
                  {typeOperation === 'entree'
                    ? <><TrendingUp size={16}/> Entrée de stock</>
                    : <><TrendingDown size={16}/> Sortie de stock</>}
                </h2>
                <p className="sk-modal-sub">
                  {typeOperation === 'entree' ? 'Enregistrer une réception de marchandise' : 'Enregistrer une sortie de stock'}
                </p>
              </div>
              <button className="sk-modal-close" onClick={() => { setShowModal(false); resetForm(); }}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit} className="sk-modal-body">

              {/* Boutique filter */}
              {['admin-entreprise','super-admin'].includes(user?.role) && boutiques.length > 0 && (
                <div className="sk-field">
                  <label>Filtrer par boutique</label>
                  <select value={formData.boutiqueIdModal}
                    onChange={e => { setFormData({...formData, boutiqueIdModal: e.target.value, produit: ''}); setSelectedProduit(null); }}>
                    <option value="">Toutes les boutiques</option>
                    {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}</option>)}
                  </select>
                </div>
              )}

              {/* Produit (combobox recherche) */}
              <div className="sk-field">
                <label>Produit *</label>
                <div className="sk-combobox">
                  <Search size={13} className="sk-combobox-ico"/>
                  <input
                    type="text"
                    className="sk-combobox-input"
                    placeholder="Rechercher par nom, code ou code-barres…"
                    value={produitSearch}
                    onChange={e => { setProduitSearch(e.target.value); setShowProduitList(true); if (formData.produit) { setFormData({ ...formData, produit: '' }); setSelectedProduit(null); } }}
                    onFocus={() => setShowProduitList(true)}
                    onBlur={() => setTimeout(() => setShowProduitList(false), 180)}
                    autoComplete="off"
                  />
                  {produitSearch && (
                    <button type="button" className="sk-combobox-clear"
                      onClick={() => { setProduitSearch(''); setFormData({ ...formData, produit: '' }); setSelectedProduit(null); setShowProduitList(true); }}>
                      <X size={11}/>
                    </button>
                  )}
                  {showProduitList && (
                    <div className="sk-combobox-list">
                      {produitsModalSearched.length === 0 ? (
                        <div className="sk-combobox-empty">Aucun produit trouvé</div>
                      ) : produitsModalSearched.slice(0, 50).map(p => {
                        let stockDisplay = `${p.quantiteStock} u`;
                        if (p.typeVente === 'gros' && p.nombrePiecesParCarton > 1) {
                          const ct = Math.floor(p.quantiteStock / p.nombrePiecesParCarton);
                          const rest = p.quantiteStock % p.nombrePiecesParCarton;
                          stockDisplay = `${ct} ct${rest > 0 ? ` +${rest}u` : ''}`;
                        }
                        return (
                          <div key={p._id}
                            className={`sk-combobox-item${formData.produit === p._id ? ' sk-combobox-item--active' : ''}`}
                            onMouseDown={() => handleProduitChange(p._id)}>
                            <div className="sk-combobox-item-main">
                              <span className="sk-combobox-item-nom">{p.nom}</span>
                              <span className="sk-combobox-item-code">{p.codeProduit}</span>
                            </div>
                            <div className="sk-combobox-item-meta">
                              <span className={`sk-type-vente ${p.typeVente === 'gros' ? 'sk-type-vente--gros' : 'sk-type-vente--detail'}`}>
                                {p.typeVente === 'gros' ? 'Gros' : 'Détail'}
                              </span>
                              <span className="sk-combobox-item-stock">{stockDisplay}</span>
                            </div>
                          </div>
                        );
                      })}
                      {produitsModalSearched.length > 50 && (
                        <div className="sk-combobox-more">+{produitsModalSearched.length - 50} autres résultats… affinez la recherche</div>
                      )}
                    </div>
                  )}
                  {/* Champ caché pour valider l'obligation HTML */}
                  <input type="hidden" value={formData.produit} required/>
                </div>
              </div>

              {/* Produit info card */}
              {selectedProduit && (
                <div className="sk-prod-card">
                  <Package size={14}/>
                  <div>
                    <strong>{selectedProduit.nom}</strong>
                    <span className="sk-prod-stock"> — Stock actuel : {selectedProduit.quantiteStock} unités</span>
                    <div className="sk-prod-prix">
                      Prix d'achat : {fmt(prixProduit(selectedProduit, formData.typeVente, 'entree'))}
                      {' · '}Prix de vente : {fmt(prixProduit(selectedProduit, formData.typeVente, 'sortie'))}
                    </div>
                  </div>
                </div>
              )}

              <div className="sk-form-row">
                {/* Type de vente */}
                <div className="sk-field">
                  <label>Type de vente *</label>
                  <select value={formData.typeVente} required
                    disabled={selectedProduit?.typeVente === 'detail'}
                    onChange={e => handleTypeVenteChange(e.target.value)}>
                    <option value="detail">Détail</option>
                    <option value="gros">Gros (Carton)</option>
                  </select>
                  {formData.typeVente === 'gros' && selectedProduit && (
                    <p className="sk-field-hint">1 carton = {selectedProduit.nombrePiecesParCarton || '?'} pièces</p>
                  )}
                </div>

                {/* Motif */}
                <div className="sk-field">
                  <label>Motif *</label>
                  <select value={formData.motif} onChange={e => setFormData({...formData, motif: e.target.value})} required>
                    <option value="">Sélectionner</option>
                    {typeOperation === 'entree' ? (
                      <><option value="achat">Achat</option><option value="retour">Retour</option><option value="ajustement">Ajustement</option></>
                    ) : (
                      <><option value="vente">Vente</option><option value="perte">Perte</option><option value="casse">Casse</option><option value="ajustement">Ajustement</option></>
                    )}
                  </select>
                </div>
              </div>

              <div className="sk-form-row">
                {/* Quantité */}
                <div className="sk-field">
                  <label>Quantité *</label>
                  <input type="number" min="1" required value={formData.quantite}
                    onChange={e => setFormData({...formData, quantite: e.target.value})} placeholder="0"/>
                </div>

                {/* Prix unitaire */}
                <div className="sk-field">
                  <label>Prix unitaire (XOF) *</label>
                  <input type="number" min="0" step="0.01" required value={formData.prixUnitaire}
                    onChange={e => setFormData({...formData, prixUnitaire: e.target.value})} placeholder="0"/>
                </div>
              </div>

              {/* Valeur calculée */}
              {formData.quantite && formData.prixUnitaire && (
                <div className="sk-calc-total">
                  <span>Valeur totale estimée</span>
                  <strong>{fmt(parseFloat(formData.quantite) * parseFloat(formData.prixUnitaire))}</strong>
                </div>
              )}

              {/* Fournisseur (si motif achat) */}
              {typeOperation === 'entree' && formData.motif === 'achat' && (
                <div className="sk-field">
                  <label>Fournisseur <span className="sk-opt">(optionnel)</span></label>
                  <select value={formData.fournisseur} onChange={e => setFormData({...formData, fournisseur: e.target.value})}>
                    <option value="">Aucun fournisseur</option>
                    {fournisseurs.map(f => <option key={f._id} value={f._id}>{f.nom}</option>)}
                  </select>
                </div>
              )}

              {/* Entrepôt */}
              <div className="sk-field">
                <label>Entrepôt *</label>
                <select value={formData.entrepot} onChange={e => handleEntrepotChange(e.target.value)} required>
                  <option value="">Sélectionner un entrepôt</option>
                  {filteredEntrepots.map(e => <option key={e._id} value={e._id}>{e.nom}</option>)}
                </select>
                {filteredEntrepots.length === 0 && (
                  <p className="sk-field-warn"><AlertCircle size={12}/> Aucun entrepôt actif disponible</p>
                )}
              </div>

              {/* Emplacement (entrée) */}
              {typeOperation === 'entree' && formData.entrepot && (
                <div className="sk-field">
                  <label>Emplacement *</label>
                  <select value={formData.emplacement || 'non-range'} onChange={e => setFormData({...formData, emplacement: e.target.value})} required>
                    {emplacementOptions.duProduit.length > 0 && (
                      <optgroup label="Emplacements du produit">
                        {emplacementOptions.duProduit.map(x => (
                          <option key={x.emplacementId} value={x.emplacementId}>
                            {String(emplacementOptions.habituelId) === String(x.emplacementId) ? '★ ' : ''}{libelleEmp(x)} ({x.quantiteStock} u)
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {emplacementOptions.autres.length > 0 && (
                      <optgroup label="Autres emplacements">
                        {emplacementOptions.autres.map(x => <option key={x.emplacementId} value={x.emplacementId}>{libelleEmp(x)}</option>)}
                      </optgroup>
                    )}
                    <option value="non-range">Non rangé (à ranger plus tard)</option>
                  </select>
                  {(formData.emplacement || 'non-range') === 'non-range' && (
                    <p className="sk-field-warn"><AlertCircle size={12}/> Le stock ira en « Non rangé » et devra être rangé depuis la page Entrepôts</p>
                  )}
                </div>
              )}

              <div className="sk-form-row">
                {/* Référence */}
                <div className="sk-field">
                  <label>Référence <span className="sk-opt">(optionnel)</span></label>
                  <input type="text" value={formData.reference}
                    onChange={e => setFormData({...formData, reference: e.target.value})}
                    placeholder="Bon de commande, facture…"/>
                </div>
                {/* Commentaire */}
                <div className="sk-field">
                  <label>Commentaire <span className="sk-opt">(optionnel)</span></label>
                  <input type="text" value={formData.commentaire}
                    onChange={e => setFormData({...formData, commentaire: e.target.value})}
                    placeholder="Note libre…"/>
                </div>
              </div>

              <div className="sk-modal-footer">
                <button type="button" className="sk-btn-cancel"
                  onClick={() => { setShowModal(false); resetForm(); }}>Annuler</button>
                <button type="submit" className={`sk-btn-confirm sk-btn-confirm--${typeOperation}`} disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="sk-spin"/> Enregistrement…</>
                    : <><Check size={13}/> Enregistrer</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Stock;
